import React from 'react';
import PropTypes from 'prop-types';
import { ROIWrapper, MessageTitle, MessageBody, SliderContainer, ExpanderButton, ExpanderText, AdvancedConfigContainer, InputLabel, QuestionContainer, InputUnit, ChartContainer } from './ROI.styled';
import KPICard from '../KPICard/KPICard';
import { useSelector, useDispatch } from 'react-redux';
import { SubPageTitle, KPIRow, StyledTextInputAnalysis, StyledForm, AddButton, RevenueWrapper, RemoveButton, SubmitButton } from '../../pages/AnalysisPage/AnalysisPage.styled';
import { populateInputs, resetEnableReset } from '../../features/inputs/inputs';
import { ReactComponent as ChevronUp } from '../../assets/chevron-up.svg';
import { ReactComponent as ChevronDown } from '../../assets/chevron-down.svg';
import { useState } from 'react';
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import RangeSlider from 'react-bootstrap-range-slider';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Label, ResponsiveContainer, Dot, ReferenceArea } from 'recharts';


function ROI () {
   const monthlyResidentBenefit = useSelector((state) => state.outputs.value.roi.monthlyResidentBenefit);
   const monthlyBuildingBenefit = useSelector((state) => state.outputs.value.roi.monthlyBuildingBenefit);
   const netROI10Year = useSelector((state) => state.outputs.value.roi.netROI10Year);
   const yearsToBreakEven = useSelector((state) => state.outputs.value.roi.yearsToBreakEven);
   const messages = useSelector((state) => state.outputs.value.roi.messages);
   const benefitToResident = useSelector((state) => state.inputs.value.benefitToResident)*10;
   const inputs = useSelector((state) => state.inputs.value);
   const charts = useSelector((state) => state.outputs.value.roi.roiChartArray);
   const advancedConfigFields = useSelector((state) => state.inputs.advancedConfigToShow);
   const dispatch = useDispatch();
   const [advancedExpanded, setAdvancedExpanded] = useState(true);
   const [revenueExpanded, setRevenueExpanded] = useState(true);
   const rateArray = useSelector((state) => state.outputs.value.roi.rateArray);
   const markupArray = useSelector((state) => state.outputs.value.roi.markupArray);
   const [roiYears, setROIYears] = useState(inputs.roiPeriod);

   const handleAdd = (event, costList) => {
      event.preventDefault();
      event.target[0].value = event.target[0].value.replace('$', '');
      event.target[0].value = event.target[0].value.replace(',', '');
      const costObj = 
      {
         isTotal: false,
         number: Number(event.target[0].value),
         label: event.target[1].value
      }
      if (costList === "monthly") {
         dispatch(populateInputs({...inputs, roiMonthlyArr: [...inputs.roiMonthlyArr, {amount: costObj.number, title: costObj.label}]}));
         document.getElementById("monthlyRevenueForm").reset();
      } else {
         dispatch(populateInputs({...inputs, roiAnnualArr: [...inputs.roiAnnualArr, {amount: costObj.number, title: costObj.label}]}));
         document.getElementById("annualRevenueForm").reset();
      }
      dispatch(resetEnableReset(true));
   }
   
   const handleRemove = (event, costList, costToRemove) => {
      if (costList === "monthly") {
         dispatch(populateInputs({...inputs, roiMonthlyArr: inputs.roiMonthlyArr.filter((cost) => cost !== costToRemove)}));
      } else {
         dispatch(populateInputs({...inputs, roiAnnualArr: inputs.roiAnnualArr.filter((cost) => cost !== costToRemove)}));
      }
      dispatch(resetEnableReset(true));
   }

   const CustomizedDot = (props) => {
      const { cx, cy, stroke, payload, value } = props;
    
      if (value === 0) {
        return (
         <Dot cx={cx} cy={cy} r={6} stroke={stroke} fill="#3A9A83" ></Dot>
        );
      }
    
      return (
         <Dot cx={cx} cy={cy} r={3} fill="#B2B2B2202" ></Dot>
      );
    };

   const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
         const numFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
        return (
          <div style={{ backgroundColor: "white", opacity: '0.9', padding: '10px', color: '#173346', borderRadius: 5, stroke: 'bold', width: '150px', height: '100px' }}>
            <p >{`Year ${label}`}</p>
            <p>{`Net ROI: ${numFormat.format(payload[0].value)}`}</p>
         </div>
        );
      }
    
      return null;
    };

   return (
      <ROIWrapper>
         Values will update as you adjust the slider below. Some slider positions may result in blank values.
         <KPIRow width='75%'>
            {yearsToBreakEven[benefitToResident] ? <KPICard key={0} isTotal={false} number={`${yearsToBreakEven[benefitToResident].toLocaleString(undefined, {maximumFractionDigits: 1})}`} label={"Years to Break Even"} /> : null}
            <KPICard key={1} isTotal={false} number={`${netROI10Year[benefitToResident] > 0 ? `${netROI10Year[benefitToResident].toLocaleString(undefined, {maximumFractionDigits: 0})}%`: '--'}`} label={`Net ROI Over ${roiYears} Years`} />
            <KPICard key={2} isTotal={false} number={`$${monthlyBuildingBenefit[benefitToResident].toLocaleString(undefined, {maximumFractionDigits: 0})}`} label={"Estimated Monthly Benefit to Property Owners"} />
            <KPICard key={3} isTotal={false} number={`$${monthlyResidentBenefit[benefitToResident].toLocaleString(undefined, {maximumFractionDigits: 0})}`} label={"Estimated Monthly Resident Benefit"} />
         </KPIRow>
         <SubPageTitle>Revenue</SubPageTitle>
         {messages.map((message, index) => (
            <>
            <MessageTitle>{message.title}</MessageTitle>
            <MessageBody key={index}>{message.message}</MessageBody>
            </>
         ))}
         <SliderContainer>
         <div>${rateArray[0].toLocaleString(undefined, {minimumFractionDigits: 2})} per kWh</div>
            <RangeSlider 
               style={{height: '100%', verticalAlign: 'middle'}}
               size='sm'
               variant='secondary'
               tooltip='off'
               step={0.1} 
               min={0} 
               max={1}
               tooltipPlacement='top'
               tooltipLabel={(percent) => `$${rateArray[benefitToResident].toLocaleString(undefined, {minimumFractionDigits: 2})}`}
               defaultValue={inputs.benefitToBuilding}
               onChange={(e) => 
                  {
                     dispatch(populateInputs({...inputs, benefitToResident: (Number((1 - Number(e.target.value)).toFixed(1))), benefitToBuilding: Number(Number(e.target.value).toFixed(1)) }));
                     dispatch(resetEnableReset(true));
                  }
               }
               />
            <div>${rateArray[rateArray.length - 1].toLocaleString(undefined, {minimumFractionDigits: 2})} per kWh</div>
         </SliderContainer>
         <MessageBody>Selected Value: <b>${rateArray[10 - benefitToResident].toLocaleString(undefined, {minimumFractionDigits: 2})}</b></MessageBody>

         <MessageBody>This equates to a mark-up of {(markupArray[10 - benefitToResident]*100).toLocaleString(undefined, {maximumFractionDigits: 0})}% on the average commercial rate.</MessageBody>
         {
         charts[benefitToResident] ? 
         <ChartContainer>
            <SubPageTitle>Net ROI Over Time</SubPageTitle>
            {window.innerWidth > 600 ? 
            // Full screen version
            <ResponsiveContainer width={900} aspect={2}>
            <LineChart data={charts[benefitToResident]} margin={{ top: 5, right: 5, bottom: 20, left: 50 }}>
               <ReferenceArea x2={yearsToBreakEven[benefitToResident]} fill="red" opacity={"0.2"}/>
               <ReferenceArea x1={yearsToBreakEven[benefitToResident]} fill="#E1F2E0" opacity={"0.9"}/>
               <Line type="monotone" dataKey='Net ROI' stroke="#3A9A83" activeDot={{ r: 6 }} dot={<CustomizedDot />}/>
               <CartesianGrid stroke="#B2B2B220"/>
               <XAxis stroke="#173346" domain={[0, 10]} type='number' dataKey={'year'} ticks={[yearsToBreakEven[benefitToResident]]}> 
                  <Label offset={-10} position='insideBottom' stroke="#173346">Years</Label>
               </XAxis>
               <YAxis stroke="#173346" type='number' units={100000} tickCount={5} tickFormatter={(tick) => Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tick)}>
                  <Label value='Net ROI ($)' angle={-90} position='insideLeft' offset={-30} stroke="#173346"/>
               </YAxis>
               <Tooltip content={<CustomTooltip />}/>
            </LineChart>
            </ResponsiveContainer> : 
            // Mobile version
            <ResponsiveContainer width={350} height={300}>
            <LineChart data={charts[benefitToResident]} margin={{ top: 5, right: 5, bottom: 20, left: 50 }}>
               <ReferenceArea x2={yearsToBreakEven[benefitToResident]} fill="red" opacity={"0.2"}/>
               <ReferenceArea x1={yearsToBreakEven[benefitToResident]} fill="#E1F2E0" opacity={"0.9"}/>
               <Line type="monotone" dataKey='Net ROI' stroke="#3A9A83" activeDot={{ r: 6 }} dot={<CustomizedDot />}/>
               <CartesianGrid stroke="#B2B2B220"/>
               <XAxis stroke="#173346" domain={[0, 10]} type='number' dataKey={'year'} ticks={[yearsToBreakEven[benefitToResident]]}> 
                  <Label offset={-10} position='insideBottom' stroke="#173346">Years</Label>
               </XAxis>
               <YAxis stroke="#173346" type='number' units={100000} tickCount={5} tickFormatter={(tick) => Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tick)}>
                  <Label value='Net ROI ($)' angle={-90} position='insideLeft' offset={-30} stroke="#173346"/>
               </YAxis>
               <Tooltip content={<CustomTooltip />}/>
            </LineChart>
            </ResponsiveContainer>
            }
         </ChartContainer>
          : null
         }
         <ExpanderText
            onClick={() => setAdvancedExpanded(!advancedExpanded)}
         >
            <SubPageTitle>Advanced Configurations</SubPageTitle>
            <ExpanderButton>
               {advancedExpanded === true ? <ChevronUp /> : <ChevronDown />}
            </ExpanderButton>
         <b>To see your changes reflected in the metrics, click "Recalculate" above.</b>
         </ExpanderText>
         <AdvancedConfigContainer expanded={advancedExpanded}>
         {advancedConfigFields.map((field, index) => (
            <QuestionContainer>
            <InputLabel><b>{field.placeholder}: </b></InputLabel>
            <InputUnit><StyledTextInputAnalysis
                     id={field.id}
                     placeholder={field.placeholder}
                     defaultValue={field.id === 'annualVehiclesMiles' ? field.defaultValue.toLocaleString('en') : field.defaultValue}
                     min={field.range[0]}
                     max={field.range[1]}
                     isValid={inputs[field.id] >= field.range[0] && inputs[field.id] <= field.range[1]}
                     // type={'number'}
                     inputmode='decimal'
                     width='50%'
                     pattern='(?<=\s|^)(?:\d{1,3}(?:,\d{3})*(?:\.\d+)?|\.\d+)(?=\s|$)'
                     step={field.allowDecimals ? 0.01 : 1}
                     onChange={(e) => {
                        dispatch(populateInputs({...inputs, [field.id]: !isNaN(Number(e.target.value.replace(',', ''))) ? Number(e.target.value.replace(',', '')) : null}));
                        dispatch(resetEnableReset(true));
                     }}
               />
            {field.units}</InputUnit>
            <InputLabel>Range: {field.range[0].toLocaleString('en')} to {field.range[1].toLocaleString('en')}</InputLabel>
            <InputLabel dangerouslySetInnerHTML={{__html: field.text}} />
         </QuestionContainer>            
         ))}
         </AdvancedConfigContainer>
         <ExpanderText
            onClick={() => setRevenueExpanded(!revenueExpanded)}
         >
            <SubPageTitle>Additional Expected Revenue/Funding</SubPageTitle>
            <ExpanderButton>
               {revenueExpanded === true ? <ChevronUp /> : <ChevronDown />}
            </ExpanderButton>
         </ExpanderText>
         <AdvancedConfigContainer expanded={revenueExpanded}>
         <MessageTitle>Additional Annual Revenue: </MessageTitle>
         {inputs.roiAnnualArr.map((revenue, index) => (
            <RevenueWrapper>
               <RemoveButton onClick={(e) => handleRemove(e, "annual", revenue)}>x</RemoveButton>
               <MessageBody><b>{revenue.title}:</b> ${revenue.amount}</MessageBody>
            </RevenueWrapper>
         ))}
         <StyledForm width='50%' onSubmit={(e) => handleAdd(e, "annual")} id="annualRevenueForm">
            <StyledTextInputAnalysis
                  id="monthlyCosts"
                  placeholder="$"
                  width='10%'
            />
            <StyledTextInputAnalysis
                  id="monthlyCostDescription"
                  placeholder="Description"
                  aria-label='Description'
                  width='50%'
            />
            <AddButton type="submit">Add</AddButton>
         </StyledForm>
         <MessageTitle>Additional Monthly Revenue: </MessageTitle>
         {inputs.roiMonthlyArr.map((revenue, index) => (
            <RevenueWrapper>
               <RemoveButton onClick={(e) => handleRemove(e, "monthly", revenue)}>x</RemoveButton>
               <MessageBody><b>{revenue.title}:</b> ${revenue.amount}</MessageBody>
            </RevenueWrapper>
         ))}
         <StyledForm width='50%' onSubmit={(e) => handleAdd(e, "monthly")} id="monthlyRevenueForm">
            <StyledTextInputAnalysis
                  id="monthlyCosts"
                  placeholder="$"
                  width={'10%'}
                  type='number'
            />
            <StyledTextInputAnalysis
                  id="monthlyCostDescription"
                  placeholder="Description"
                  aria-label='Description'
                  width={'50%'}
            />
            <AddButton type="submit">Add</AddButton>
         </StyledForm>
         </AdvancedConfigContainer>
      </ROIWrapper>
)};

ROI.propTypes = {};

ROI.defaultProps = {};

export default ROI;
